/**
 * TestDataVisibilityContext — global "Show test data" toggle.
 *
 * Available to every logged-in user. Default: off.
 *
 * Toggle is EXCLUSIVE: ON ⇒ list endpoints return ONLY test rows;
 * OFF ⇒ ONLY real rows. There is no "show both at once" mode. List
 * hooks read this and append `?include_test=true|false` to fetches.
 *
 * Persisted per-user-per-device via localStorage.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

const STORAGE_KEY = 'daccelerate_v2_show_test_data'

interface TestDataVisibilityContextValue {
  showTestData: boolean
  setShowTestData(next: boolean): void
}

const TestDataVisibilityContext = createContext<TestDataVisibilityContextValue | null>(null)

// Safe default — show-test-data off, setter is a no-op. Lets test
// harnesses and any non-wrapped call site import data hooks (useDemands
// / useApplications) without erroring out. In real usage every protected
// route IS wrapped by TestDataVisibilityProvider (see App.tsx).
const DEFAULT_VISIBILITY: TestDataVisibilityContextValue = {
  showTestData: false,
  setShowTestData: () => { /* no-op outside provider */ },
}

export function useTestDataVisibility(): TestDataVisibilityContextValue {
  return useContext(TestDataVisibilityContext) ?? DEFAULT_VISIBILITY
}

function readStorage(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

export function TestDataVisibilityProvider({ children }: { children: ReactNode }) {
  const [showTestData, setShowTestDataState] = useState<boolean>(() => readStorage())

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(showTestData))
    } catch {
      /* localStorage unavailable — ignore */
    }
  }, [showTestData])

  return (
    <TestDataVisibilityContext.Provider
      value={{ showTestData, setShowTestData: setShowTestDataState }}
    >
      {children}
    </TestDataVisibilityContext.Provider>
  )
}
